import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import moment from 'moment';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ReplayIcon from '@material-ui/icons/Replay';
import Box from '@material-ui/core/Box';
import SimpleTable from './SimpleTable';
import ThemedSpinner from '../themed/ThemedSpinner';

const STATUS_LABEL = {
    1: 'running',
    2: 'done',
    3: 'failed',
};

function BatchSyncReplay({
    companyId,
    results,
    loading,
    replaying,
    lastResult,
    error,
    onLoad,
    onReplay,
    onConfirmApply,
}) {
    const [sourceId, setSourceId] = useState('');
    const [preview, setPreview] = useState(true);

    useEffect(() => {
        if (companyId) onLoad(companyId);
    }, [companyId]);

    const columns = useMemo(
        () => [
            {
                Header: 'When',
                accessor: 'created',
                Cell: (x) =>
                    x.value ? moment(x.value).format('YYYY-MM-DD HH:mm:ss') : '',
            },
            {
                Header: 'Id',
                accessor: 'id',
                Cell: (x) => (
                    <Button
                        size="small"
                        onClick={() => setSourceId(x.value)}
                    >
                        {x.value}
                    </Button>
                ),
            },
            {
                Header: 'Status',
                accessor: 'status',
                Cell: (x) => STATUS_LABEL[x.value] || x.value,
            },
            {
                Header: 'Users +/−',
                id: 'usersDelta',
                accessor: (row) =>
                    `${row.createdUsers || 0}/${row.removedUsers || 0}`,
            },
            {
                Header: 'Membership +/−',
                id: 'membershipDelta',
                accessor: (row) =>
                    `${row.createdMembership || 0}/${
                        row.removedMembership || 0
                    }`,
            },
            {
                Header: 'Initiator',
                accessor: 'initiator',
            },
        ],
        []
    );

    const replay = async () => {
        const id = (sourceId || '').trim();
        if (!id) return;
        if (preview) {
            await onReplay(companyId, id, true);
            return;
        }
        onConfirmApply(
            `Re-run batch-sync ${id} for company ${companyId}? This writes users and memberships from the stored JSON.`,
            async () => {
                await onReplay(companyId, id, false);
            }
        );
    };

    return (
        <Accordion defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h4">Batch-sync replay</Typography>
            </AccordionSummary>
            <AccordionDetails style={{ display: 'block' }}>
                <Typography variant="body2" gutterBottom>
                    Re-run a JSON import from the payload stored on the
                    integration result. Paste the result id, or click a recent
                    run.
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={7}>
                        <TextField
                            fullWidth
                            label="integration_result id"
                            value={sourceId}
                            onChange={(e) => setSourceId(e.target.value)}
                            placeholder="2f03ef7e-0ec1-404a-b628-73243ebeff4a"
                        />
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            control={
                                <Switch
                                    color="primary"
                                    checked={preview}
                                    onChange={(e) =>
                                        setPreview(e.target.checked)
                                    }
                                />
                            }
                            label="Preview only"
                        />
                    </Grid>
                    <Grid item>
                        {replaying ? (
                            <ThemedSpinner />
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<ReplayIcon />}
                                onClick={replay}
                                disabled={!sourceId.trim()}
                            >
                                Replay
                            </Button>
                        )}
                    </Grid>
                </Grid>
                {error && (
                    <Box mt={2}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                )}
                {lastResult && (
                    <Box mt={2}>
                        <Typography variant="body2">
                            Queued {lastResult.preview ? 'preview' : 'sync'}{' '}
                            {lastResult.latestRunningSyncId} (
                            {lastResult.usersCount} users,{' '}
                            {lastResult.groupsCount} groups)
                        </Typography>
                    </Box>
                )}
                <Box mt={3}>
                    {loading ? (
                        <ThemedSpinner />
                    ) : (
                        <SimpleTable
                            columns={columns}
                            data={results || []}
                            pageSize={10}
                            needsFilter={false}
                        />
                    )}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
}

export default observer(BatchSyncReplay);
